
import { useState } from 'react'
import useSWR from 'swr'
import {
  BrainCircuit, Plus, Server, Check, X, Settings2, Zap,
  ArrowUpDown, ExternalLink, Globe, Activity, RefreshCw,
  AlertCircle, ChevronRight, Cpu, Radio,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AI_PROVIDERS } from '@/lib/constants'

interface Provider {
  id: string
  name: string
  provider_type: string
  endpoint: string
  model_list: string
  is_active: number
  created_at: string
  priority: number
}

const fetcher = (url: string) => fetch(url).then(r => r.json())

const PROVIDER_ICONS: Record<string, React.ElementType> = {
  'cloudflare-ai': Cpu,
  'openai': BrainCircuit,
  'anthropic': Radio,
  'ollama': Server,
  'vercel-ai-gateway': Zap,
  'custom': Globe,
}

const PROVIDER_COLORS: Record<string, string> = {
  'cloudflare-ai': 'text-lava bg-lava/10 border-lava/20',
  'openai': 'text-success bg-success/10 border-success/20',
  'anthropic': 'text-cyan-accent bg-cyan-accent/10 border-cyan-accent/20',
  'ollama': 'text-warning bg-warning/10 border-warning/20',
  'vercel-ai-gateway': 'text-foreground bg-muted border-border',
  'custom': 'text-muted-foreground bg-muted border-border',
}

function ProviderCard({ provider, onToggle }: {
  provider: Provider
  onToggle: (id: string, active: boolean) => void
}) {
  const models: string[] = JSON.parse(provider.model_list || '[]')
  const Icon = PROVIDER_ICONS[provider.provider_type] ?? Globe
  const colorClass = PROVIDER_COLORS[provider.provider_type] ?? ''
  const isActive = provider.is_active === 1

  return (
    <Card className={`bg-card border transition-all ${isActive ? 'border-border hover:border-lava/30' : 'border-border/40 opacity-60'}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-md border flex-shrink-0 ${colorClass}`}>
            <Icon className="w-4 h-4" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-foreground">{provider.name}</h3>
                <p className="text-[10px] font-mono text-muted-foreground truncate max-w-xs mt-0.5">{provider.endpoint}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge variant="outline" className="text-[9px] px-1.5 h-4 font-mono border-border text-muted-foreground">
                  P{provider.priority}
                </Badge>
                <Switch
                  checked={isActive}
                  onCheckedChange={(v) => onToggle(provider.id, v)}
                  className="scale-75 data-[state=checked]:bg-lava"
                />
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-1">
              {models.slice(0, 4).map(m => (
                <code key={m} className="text-[9px] px-1.5 py-0.5 bg-muted border border-border rounded font-mono text-muted-foreground truncate max-w-[180px]">
                  {m}
                </code>
              ))}
              {models.length > 4 && (
                <span className="text-[9px] text-muted-foreground">+{models.length - 4} more</span>
              )}
            </div>

            <div className="flex items-center gap-3 mt-2.5">
              <span className={`flex items-center gap-1 text-[10px] ${isActive ? 'text-success' : 'text-muted-foreground'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-success animate-pulse' : 'bg-muted-foreground'}`} />
                {isActive ? 'Active' : 'Disabled'}
              </span>
              <Separator orientation="vertical" className="h-3" />
              <span className="text-[10px] text-muted-foreground">{models.length} models</span>
              <Separator orientation="vertical" className="h-3" />
              <span className="text-[10px] text-muted-foreground font-mono">{provider.provider_type}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function GatewayPage() {
  const { data, mutate } = useSWR<{ providers: Provider[] }>('/api/providers', fetcher)
  const [addOpen, setAddOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [testResult, setTestResult] = useState<Record<string, 'ok' | 'fail' | 'testing'>>({})

  // Add provider form
  const [newProvider, setNewProvider] = useState({
    name: '',
    provider_type: 'openai',
    endpoint: '',
    models: '',
  })

  const providers = data?.providers ?? []

  async function handleAdd() {
    setAdding(true)
    try {
      const res = await fetch('/api/providers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProvider.name,
          provider_type: newProvider.provider_type,
          endpoint: newProvider.endpoint,
          model_list: newProvider.models.split(',').map(m => m.trim()).filter(Boolean),
        }),
      })
      if (res.ok) {
        setAddOpen(false)
        setNewProvider({ name: '', provider_type: 'openai', endpoint: '', models: '' })
        mutate()
      }
    } finally {
      setAdding(false)
    }
  }

  async function handleToggle(id: string, active: boolean) {
    await fetch(`/api/providers/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: active }),
    })
    mutate()
  }

  async function handleTest(provider: Provider) {
    setTestResult(v => ({ ...v, [provider.id]: 'testing' }))
    try {
      const res = await fetch(`${provider.endpoint}/models`, {
        headers: { Authorization: `Bearer ${process.env.NEXT_PUBLIC_TEST_KEY ?? 'test'}` },
        signal: AbortSignal.timeout(5000),
      })
      setTestResult(v => ({ ...v, [provider.id]: res.ok ? 'ok' : 'fail' }))
    } catch {
      setTestResult(v => ({ ...v, [provider.id]: 'fail' }))
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-lava/10">
            <BrainCircuit className="w-4 h-4 text-lava" />
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">AI Gateway</h2>
            <p className="text-xs text-muted-foreground">
              {providers.filter(p => p.is_active).length} active providers · Cloudflare AI Gateway routing
            </p>
          </div>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          size="sm"
          className="gap-2 lava-gradient text-lava-foreground border-0 text-xs hover:opacity-90"
        >
          <Plus className="w-3.5 h-3.5" /> Add Provider
        </Button>
      </div>

      <Tabs defaultValue="providers">
        <TabsList className="bg-muted border border-border h-8 p-0.5">
          <TabsTrigger value="providers" className="text-xs h-7 px-3 data-[state=active]:bg-card">Providers</TabsTrigger>
          <TabsTrigger value="routing" className="text-xs h-7 px-3 data-[state=active]:bg-card">Routing</TabsTrigger>
          <TabsTrigger value="cf-gateway" className="text-xs h-7 px-3 data-[state=active]:bg-card">CF AI Gateway</TabsTrigger>
        </TabsList>

        {/* Providers tab */}
        <TabsContent value="providers" className="mt-4 space-y-3">
          {/* Available presets */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Supported Providers</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {AI_PROVIDERS.map(p => {
                const Icon = PROVIDER_ICONS[p.id] ?? Globe
                return (
                  <button
                    key={p.id}
                    onClick={() => {
                      setNewProvider(v => ({ ...v, name: p.name, provider_type: p.id, endpoint: p.endpoint, models: p.models.join(', ') }))
                      setAddOpen(true)
                    }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-lg bg-muted/50 border border-border hover:border-lava/40 hover:bg-lava/5 transition-all group text-center"
                  >
                    <div className="p-1.5 rounded-md bg-background border border-border group-hover:border-lava/30 transition-colors">
                      <Icon className="w-4 h-4 text-muted-foreground group-hover:text-lava transition-colors" />
                    </div>
                    <span className="text-[10px] font-medium text-foreground leading-tight">{p.name}</span>
                  </button>
                )
              })}
            </div>
          </div>

          <Separator className="bg-border" />

          {/* Active providers */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Configured Providers</p>
            {providers.length === 0 ? (
              <Card className="border-dashed border-border bg-card">
                <CardContent className="py-10 text-center">
                  <BrainCircuit className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
                  <p className="text-sm text-foreground font-medium">No providers configured</p>
                  <p className="text-xs text-muted-foreground mt-1">Add a provider above to start routing AI requests</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {providers.map(p => (
                  <ProviderCard key={p.id} provider={p} onToggle={handleToggle} />
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Routing tab */}
        <TabsContent value="routing" className="mt-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-lava" />
                Request Routing Rules
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Requests are routed based on key-level provider assignment, then gateway priority order.
              </p>
              <div className="space-y-2">
                {[
                  { step: '1', rule: 'API key has explicit provider_id set', action: 'Route to that provider' },
                  { step: '2', rule: 'No provider set on key', action: 'Route to highest-priority active provider' },
                  { step: '3', rule: 'No active providers', action: 'Route to Cloudflare Workers AI (default)' },
                  { step: '4', rule: 'Model not found on provider', action: 'Failover to next priority provider' },
                ].map((r) => (
                  <div key={r.step} className="flex items-start gap-3 p-3 rounded-md bg-muted/40 border border-border">
                    <span className="text-[10px] font-mono font-bold text-lava w-4 flex-shrink-0 mt-0.5">{r.step}</span>
                    <div className="flex-1 flex items-start gap-2">
                      <span className="text-xs text-foreground flex-1">{r.rule}</span>
                      <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-lava font-medium">{r.action}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CF AI Gateway tab */}
        <TabsContent value="cf-gateway" className="mt-4 space-y-3">
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Cpu className="w-4 h-4 text-lava" />
                Cloudflare AI Gateway Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { label: 'Gateway Name', value: 'd1-rest-lavaflowstreams', mono: true },
                  { label: 'Worker Binding', value: 'LAVAFLOW_SERVICE_BINDING', mono: true },
                  { label: 'Workers AI Binding', value: 'LAVAFLOW_WORKERS_AI', mono: true },
                  { label: 'Secrets Store Binding', value: 'LAVAFLOW_SECRET', mono: true },
                  { label: 'Rate Limiter', value: 'LAVAFLOW_RATE_LIMIT (NS #7)', mono: true },
                  { label: 'Analytics Engine', value: 'LAVAFLOW_ANALYTICS', mono: true },
                ].map(item => (
                  <div key={item.label} className="p-3 rounded-md bg-muted/50 border border-border">
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground/70 font-medium mb-1">{item.label}</p>
                    <code className="text-xs font-mono text-foreground">{item.value}</code>
                  </div>
                ))}
              </div>

              <Separator />

              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-cyan-accent/5 border border-cyan-accent/20">
                <Activity className="w-4 h-4 text-cyan-accent flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-foreground">Custom Provider Gateway</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Register your own provider endpoint within the Cloudflare AI Gateway to proxy and monitor all requests.
                    Use your API key with <code className="font-mono text-lava">cf-aig-authorization</code> header for authenticated routing.
                  </p>
                </div>
              </div>

              <div className="rounded-md bg-muted border border-border overflow-hidden">
                <div className="px-3 py-1.5 bg-muted/80 border-b border-border flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground font-mono">curl — CF AI Gateway request</span>
                </div>
                <pre className="p-3 text-[11px] font-mono overflow-x-auto text-foreground leading-relaxed">
{`curl https://gateway.ai.cloudflare.com/v1/\\
  {CLOUDFLARE_ACCOUNT_ID}/lavaflow-gateway/workers-ai \\
  -H "Authorization: Bearer {LAVAFLOW_SECRET}" \\
  -H "cf-aig-authorization: Bearer {CF_AIG_TOKEN}" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"@cf/meta/llama-3.1-8b-instruct","messages":[{"role":"user","content":"Hello"}]}'`}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add provider dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <BrainCircuit className="w-4 h-4 text-lava" />
              Configure AI Provider
            </DialogTitle>
            <DialogDescription className="text-xs">
              Add an OpenAI-compatible provider endpoint to route AI requests through the LavaFlow gateway.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Provider Type</Label>
              <Select value={newProvider.provider_type} onValueChange={v => setNewProvider(p => ({ ...p, provider_type: v }))}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AI_PROVIDERS.map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Display Name</Label>
              <Input value={newProvider.name} onChange={e => setNewProvider(p => ({ ...p, name: e.target.value }))} className="h-8 text-xs" placeholder="My OpenAI Provider" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Endpoint URL</Label>
              <Input value={newProvider.endpoint} onChange={e => setNewProvider(p => ({ ...p, endpoint: e.target.value }))} className="h-8 text-xs font-mono" placeholder="https://api.openai.com/v1" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Models (comma-separated)</Label>
              <Input value={newProvider.models} onChange={e => setNewProvider(p => ({ ...p, models: e.target.value }))} className="h-8 text-xs font-mono" placeholder="gpt-4o, gpt-4o-mini" />
            </div>

            <Button
              onClick={handleAdd}
              disabled={adding || !newProvider.name || !newProvider.endpoint}
              className="w-full h-8 lava-gradient text-lava-foreground border-0 text-xs hover:opacity-90"
            >
              {adding ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
              Add Provider
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
